
import { SimulationConfig, EscapeRing, MorphData } from '../types';
import { ShapeUtils } from '../logic/physics/ShapeUtils';
import { lerpColor } from '../lib/colorUtils';

export const drawEscapeRings = (
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  rings: EscapeRing[],
  config: SimulationConfig,
  globalHue: number,
  morph?: MorphData,
  onlyGuards: boolean = false,
  cam?: any,
  width: number = 0,
  height: number = 0
) => {
  const visibleLimit = Math.min(rings.length, config.escapeMaxVisibleRings);
  const useRGB = config.rgbLighting && config.useContainerRGB;
  const totalRings = config.escapeRingCount;
  const oppositeCount = config.escapeOppositeGapCount || 0;
  
  const shape = morph ? morph.from : (config.containerShape || 'CIRCLE');
  const targetShape = morph ? morph.to : shape;
  const tMorph = morph ? morph.factor : 0;
  const rotRad = (config.containerRotation || 0) * Math.PI / 180;
  
  const isCircle = shape === 'CIRCLE' && targetShape === 'CIRCLE';
  const isLinear = ShapeUtils.isLinearShape(shape);
  const useScaling = isLinear; 

  const hyperSpeedActive = config.enableHyperSpeed && rings.length < config.escapeRingCount && rings.length > 0;
  const simplified = config.hyperSpeedSimplifiedVisuals && hyperSpeedActive;

  const USE_LUT = (!isLinear && !isCircle) || simplified;
  
  const LUT_RES = simplified ? 180 : 720;
  let unitLUT: Float32Array | null = null;
  if (USE_LUT) {
      unitLUT = ShapeUtils.generateUnitRadiusLUT(shape, targetShape, tMorph, LUT_RES);
  }

  const TwoPI = Math.PI * 2;
  
  let minVisibleR = 0;
  let maxVisibleR = Infinity;
  const margin = config.frustumCullingMargin || 100;

  if (config.enableFrustumCulling && config.cullRingsOutsideScreen && cam) {
      const halfW = width / 2;
      const halfH = height / 2;
      const scale = cam.scale || 1;
      
      const left = -halfW / scale - cam.x;
      const right = halfW / scale - cam.x;
      const top = -halfH / scale - cam.y;
      const bottom = halfH / scale - cam.y;

      const dx = Math.max(left, 0, -right);
      const dy = Math.max(top, 0, -bottom);
      minVisibleR = Math.sqrt(dx * dx + dy * dy) - margin;

      const fX = Math.abs(left) > Math.abs(right) ? Math.abs(left) : Math.abs(right);
      const fY = Math.abs(top) > Math.abs(bottom) ? Math.abs(top) : Math.abs(bottom);
      maxVisibleR = Math.sqrt(fX * fX + fY * fY) + margin;
  }

  let prevNormalGapPoints: { start: {x:number, y:number}, end: {x:number, y:number} } | null = null;
  let prevOppositeGapPoints: { start: {x:number, y:number}, end: {x:number, y:number} } | null = null;
  const baseRadius = (config.mode === 'RHYTHMIC_ESCAPE' ? config.rhythmicEscapeShieldRingRadius : config.containerRadius);

  for (let idx = 0; idx < visibleLimit; idx++) {
      const ring = rings[idx];
      if (ring.radius < 2) continue;

      if (config.enableFrustumCulling && config.cullRingsOutsideScreen) {
          if (ring.radius < minVisibleR || ring.radius > maxVisibleR) {
              if (config.escapeSideGuards && config.escapeSideGuardsVisible) {
                  const currentShift = Math.max(0, totalRings - rings.length);
                  const originalIndex = currentShift + idx;
                  const isOpposite = originalIndex < oppositeCount;
                  const effectiveGapSize = isOpposite ? (config.escapeOppositeGapSize || 30) : config.escapeGapSize;
                  let halfGapRad = (effectiveGapSize * Math.PI / 180) / 2;
                  if (config.escapeGapAlignment === 'FIXED_SIZE') {
                      const baseArcLength = (effectiveGapSize * Math.PI / 180) * config.containerRadius;
                      const fixedGapAngle = baseArcLength / ring.radius;
                      halfGapRad = fixedGapAngle / 2;
                  }
                  const gapStart = ring.angle - halfGapRad;
                  const gapEnd = ring.angle + halfGapRad;
                  
                  const getR = (t: number) => {
                      let shapeAngle = (t - rotRad) % TwoPI;
                      if (shapeAngle < 0) shapeAngle += TwoPI;
                      if (USE_LUT && unitLUT) {
                          const idxL = Math.floor((shapeAngle / TwoPI) * LUT_RES) % LUT_RES;
                          const unitR = unitLUT[idxL];
                          if (useScaling) return unitR * ring.radius;
                          return (unitR * baseRadius) + (ring.radius - baseRadius);
                      } else {
                          return ShapeUtils.getInterpolatedRadiusAtAngle(shapeAngle, shape, targetShape, tMorph, ring.radius, baseRadius);
                      }
                  };
                  const rStart = getR(gapStart);
                  const rEnd = getR(gapEnd);
                  const p1x = centerX + Math.cos(gapStart) * rStart;
                  const p1y = centerY + Math.sin(gapStart) * rStart;
                  const p2x = centerX + Math.cos(gapEnd) * rEnd;
                  const p2y = centerY + Math.sin(gapEnd) * rEnd;
                  const newPoints = { start: {x: p1x, y: p1y}, end: {x: p2x, y: p2y} };
                  if (isOpposite) prevOppositeGapPoints = newPoints;
                  else prevNormalGapPoints = newPoints;
              }
              continue;
          }
      }

      const currentShift = Math.max(0, totalRings - rings.length);
      const originalIndex = currentShift + idx;
      const isOpposite = originalIndex < oppositeCount;
      const effectiveGapSize = isOpposite ? (config.escapeOppositeGapSize || 30) : config.escapeGapSize;

      let ringColor = '';
      if (config.rgbLighting && (useRGB || config.escapeColorMode === 'RAINBOW')) {
        const ringHue = (globalHue + idx * 8 * (config.escapeRainbowSpeed || 1.0)) % 360;
        ringColor = `hsl(${ringHue}, 100%, 60%)`;
      } else if (config.escapeColorMode === 'UNIFORM') {
        ringColor = config.escapeUniformColor || '#3b82f6';
      } else if (config.escapeColorMode === 'INDIVIDUAL') {
        if (config.escapeIndividualColors && (config.escapeIndividualColors as any).length > 0) {
            ringColor = (config.escapeIndividualColors as string[])[idx % (config.escapeIndividualColors as string[]).length];
        } else {
            ringColor = (config.rgbLighting && useRGB) ? `hsl(${ring.hue}, 100%, 50%)` : (config.escapeUniformColor || '#3b82f6');
        }
      } else if (config.escapeColorMode === 'ALTERNATING') {
        ringColor = idx % 2 === 0 ? (config.escapeColorA || '#ff0000') : (config.escapeColorB || '#ffffff');
      } else if (config.escapeColorMode === 'GRADIENT') {
        ringColor = lerpColor(config.escapeColorA || '#ff0000', config.escapeColorB || '#ffffff', idx / visibleLimit);
      } else {
        ringColor = config.borderColor;
      }

      const alpha = config.escapeStaticMode ? (0.85 * config.escapeRingAlphaBase) : Math.max(0.08, (config.escapeRingAlphaBase - Math.pow(idx / visibleLimit, config.escapeRingAlphaFade)));

      let halfGapRad = (effectiveGapSize * Math.PI / 180) / 2;
      if (config.escapeGapAlignment === 'FIXED_SIZE') {
          const baseArcLength = (effectiveGapSize * Math.PI / 180) * config.containerRadius;
          const fixedGapAngle = baseArcLength / ring.radius;
          halfGapRad = fixedGapAngle / 2;
      }

      const gapStart = ring.angle - halfGapRad;
      const gapEnd = ring.angle + halfGapRad;
      
      if (!onlyGuards) {
          ctx.beginPath();
          ctx.strokeStyle = ringColor;
          ctx.globalAlpha = alpha;
          ctx.lineWidth = config.escapeRingThickness * (config.escapeStaticMode ? 1 : alpha); 
          ctx.lineJoin = 'round'; 

          if (isCircle) {
              ctx.arc(centerX, centerY, ring.radius, gapEnd, gapStart); 
          } else {
              const startTheta = gapEnd;
              const totalArc = TwoPI - (2 * halfGapRad);
              const arcLength = ring.radius * totalArc;
              let drawSteps = Math.ceil(arcLength / 12);
              drawSteps = Math.max(16, Math.min(drawSteps, 200));
              const stepAngle = totalArc / drawSteps;
              
              for (let i = 0; i <= drawSteps; i++) {
                  const theta = startTheta + i * stepAngle;
                  let shapeAngle = (theta - rotRad) % TwoPI;
                  if (shapeAngle < 0) shapeAngle += TwoPI;
                  
                  let r = 0;
                  if (USE_LUT && unitLUT) {
                      const lutIndex = Math.floor((shapeAngle / TwoPI) * LUT_RES) % LUT_RES;
                      const unitR = unitLUT[lutIndex];
                      if (useScaling) r = unitR * ring.radius;
                      else r = (unitR * baseRadius) + (ring.radius - baseRadius);
                  } else {
                      r = ShapeUtils.getInterpolatedRadiusAtAngle(shapeAngle, shape, targetShape, tMorph, ring.radius, baseRadius);
                  }
                  
                  const px = centerX + Math.cos(theta) * r;
                  const py = centerY + Math.sin(theta) * r;
                  if (i === 0) ctx.moveTo(px, py);
                  else ctx.lineTo(px, py);
              }
          }
          ctx.stroke();
      }

      if (config.escapeSideGuards && config.escapeSideGuardsVisible) {
          let rStart = ring.radius;
          let rEnd = ring.radius;

          if (!isCircle) {
              const getR = (t: number) => {
                  let shapeAngle = (t - rotRad) % TwoPI;
                  if (shapeAngle < 0) shapeAngle += TwoPI;
                  if (USE_LUT && unitLUT) {
                      const idx = Math.floor((shapeAngle / TwoPI) * LUT_RES) % LUT_RES;
                      const unitR = unitLUT[idx];
                      if (useScaling) return unitR * ring.radius;
                      return (unitR * baseRadius) + (ring.radius - baseRadius);
                  } else {
                      return ShapeUtils.getInterpolatedRadiusAtAngle(shapeAngle, shape, targetShape, tMorph, ring.radius, baseRadius);
                  }
              };
              rStart = getR(gapStart);
              rEnd = getR(gapEnd);
          }

          const p1x = centerX + Math.cos(gapStart) * rStart;
          const p1y = centerY + Math.sin(gapStart) * rStart;
          const p2x = centerX + Math.cos(gapEnd) * rEnd;
          const p2y = centerY + Math.sin(gapEnd) * rEnd;

          const prevPoints = isOpposite ? prevOppositeGapPoints : prevNormalGapPoints;

          if (prevPoints) {
              ctx.beginPath();
              ctx.strokeStyle = config.escapeSideGuardColor || (config.darkMode ? '#ffffff' : '#000000'); 
              ctx.lineWidth = config.escapeSideGuardThickness || Math.max(4, config.escapeRingThickness * 1.2); 
              ctx.lineCap = 'round';
              ctx.lineJoin = 'round';
              ctx.globalAlpha = Math.min(1.0, alpha + 0.8); 
              
              ctx.moveTo(prevPoints.start.x, prevPoints.start.y);
              ctx.lineTo(p1x, p1y);
              ctx.moveTo(prevPoints.end.x, prevPoints.end.y);
              ctx.lineTo(p2x, p2y);
              ctx.stroke();
          }

          const newPoints = { start: {x: p1x, y: p1y}, end: {x: p2x, y: p2y} };
          if (isOpposite) prevOppositeGapPoints = newPoints;
          else prevNormalGapPoints = newPoints;
      } else {
          prevNormalGapPoints = null;
          prevOppositeGapPoints = null;
      }
  }
  ctx.globalAlpha = 1.0;
};
